import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

interface OtpFlowContext {
    email: string;
    identifier: string;
    userId?: string;
    expiryTime?: string;
    otpRequested: boolean;
}

interface OnboardingProgress {
    kycCompleted: boolean;
    signatoryCompleted: boolean;
}

interface OnboardingState {
    signupOtp: OtpFlowContext;
    forgotPasswordOtp: OtpFlowContext;
    onboardingProgress: OnboardingProgress;
    setSignupOtpContext: (payload: Partial<OtpFlowContext>) => void;
    clearSignupOtpContext: () => void;
    setForgotPasswordOtpContext: (payload: Partial<OtpFlowContext>) => void;
    clearForgotPasswordOtpContext: () => void;
    updateOnboardingProgress: (payload: Partial<OnboardingProgress>) => void;
    resetOnboardingProgress: () => void;
    isOnboardingPending: () => boolean;
}

const defaultOtpFlowContext: OtpFlowContext = {
    email: "",
    identifier: "",
    userId: undefined,
    expiryTime: undefined,
    otpRequested: false,
};

const defaultOnboardingProgress: OnboardingProgress = {
    kycCompleted: false,
    signatoryCompleted: false,
};

export const useOnboardingStore = create<OnboardingState>()(
    persist(
        (set, get) => ({
            signupOtp: defaultOtpFlowContext,
            forgotPasswordOtp: defaultOtpFlowContext,
            onboardingProgress: defaultOnboardingProgress,

            setSignupOtpContext: (payload) =>
                set((state) => ({
                    signupOtp: { ...state.signupOtp, ...payload },
                })),

            clearSignupOtpContext: () =>
                set({
                    signupOtp: defaultOtpFlowContext,
                }),

            setForgotPasswordOtpContext: (payload) =>
                set((state) => ({
                    forgotPasswordOtp: { ...state.forgotPasswordOtp, ...payload },
                })),

            clearForgotPasswordOtpContext: () =>
                set({
                    forgotPasswordOtp: defaultOtpFlowContext,
                }),

            updateOnboardingProgress: (payload) =>
                set((state) => ({
                    onboardingProgress: { ...state.onboardingProgress, ...payload },
                })),

            resetOnboardingProgress: () =>
                set({
                    onboardingProgress: defaultOnboardingProgress,
                }),

            isOnboardingPending: () => {
                const { onboardingProgress } = get();
                return !onboardingProgress.kycCompleted || !onboardingProgress.signatoryCompleted;
            },
        }),
        {
            name: "onboarding-store",
            storage: createJSONStorage(() => sessionStorage),
        }
    )
);
